import { config } from './config';
import { demoActive } from './modeDemo';

/**
 * LE CLIENT API — un seul point d'appel, une seule gestion d'erreur.
 *
 * Une page ne parle jamais directement au réseau : elle passe par un magasin,
 * qui passe par ici. C'est ce qui rend possible de changer l'authentification
 * ou la gestion des erreurs à un seul endroit — et de servir le MODE DÉMO
 * (DEC-025) sans qu'aucun écran le sache : en démo, chaque appel est répondu
 * par le serveur fictif du navigateur (`demo/serveur.ts`), jamais par le réseau.
 */
export class ApiError extends Error {
  constructor(
    public status: number,
    message: string,
    public details?: Array<{ path: string; message: string }>,
    /** Code machine d'un refus, quand l'écran doit y réagir (ex. `motif_requis`). */
    public code?: string,
  ) {
    super(message);
    this.name = 'ApiError';
  }
}

async function parse<T>(res: Response): Promise<T> {
  const text = await res.text();
  const data = text ? JSON.parse(text) : null;

  if (!res.ok) {
    // Le message du serveur d'abord : c'est lui qui dit ce qu'il faut corriger.
    throw new ApiError(res.status, data?.error ?? `Erreur ${res.status}`, data?.details, data?.code);
  }
  return data as T;
}

/**
 * Le transport : le réseau, ou le serveur fictif. Il rend une vraie `Response`
 * dans les deux cas, pour que la lecture des réponses et des erreurs reste la
 * même — un écran ne doit pas se comporter autrement en démo.
 */
async function transporter(method: string, path: string, init: { body?: unknown; form?: FormData } = {}): Promise<Response> {
  if (demoActive()) {
    const { repondre } = await import('@/demo/serveur');
    return repondre(method, path, init.form ?? init.body);
  }
  if (init.form) return fetch(`${config.API_BASE_URL}${path}`, { method, credentials: 'include', body: init.form });
  return fetch(`${config.API_BASE_URL}${path}`, {
    method,
    credentials: 'include',
    headers: init.body !== undefined ? { 'Content-Type': 'application/json' } : {},
    body: init.body !== undefined ? JSON.stringify(init.body) : undefined,
  });
}

async function request<T>(method: string, path: string, body?: unknown): Promise<T> {
  return parse<T>(await transporter(method, path, { body }));
}

/** Envoi d'un fichier (multipart) : les champs AVANT le fichier, l'API les lit dans cet ordre. */
async function upload<T>(path: string, fichier: File, champs: Record<string, string> = {}): Promise<T> {
  const form = new FormData();
  for (const [k, v] of Object.entries(champs)) form.append(k, v);
  form.append('fichier', fichier, fichier.name);
  return parse<T>(await transporter('POST', path, { form }));
}

/**
 * Un fichier produit par l'API, lu en mémoire : si elle refuse, son message
 * s'affiche — un lien direct aurait téléchargé le JSON de l'erreur. C'est aussi
 * ce qui permet à la démo de produire ses fichiers : un lien direct partirait
 * vers le réseau.
 */
async function fichier(path: string, nomParDefaut: string): Promise<{ blob: Blob; nom: string }> {
  const res = await transporter('GET', path);
  if (!res.ok) await parse(res);
  const nom = /filename="([^"]+)"/.exec(res.headers.get('Content-Disposition') ?? '')?.[1] ?? nomParDefaut;
  return { blob: await res.blob(), nom };
}

/** Enregistre un blob sous son nom, comme un téléchargement du navigateur. */
function enregistrer(blob: Blob, nom: string): void {
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = nom;
  a.click();
  setTimeout(() => URL.revokeObjectURL(url), 1_000);
}

async function telecharger(path: string, nomParDefaut: string): Promise<void> {
  const { blob, nom } = await fichier(path, nomParDefaut);
  enregistrer(blob, nom);
}

export const api = {
  upload,
  fichier,
  enregistrer,
  telecharger,
  get: <T>(path: string): Promise<T> => request<T>('GET', path),
  post: <T>(path: string, body?: unknown): Promise<T> => request<T>('POST', path, body),
  patch: <T>(path: string, body?: unknown): Promise<T> => request<T>('PATCH', path, body),
  put: <T>(path: string, body?: unknown): Promise<T> => request<T>('PUT', path, body),
  delete: <T>(path: string, body?: unknown): Promise<T> => request<T>('DELETE', path, body),
};
